import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Params } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBars,
  faChevronLeft,
  faChevronRight,
  faUserGraduate,
} from '@fortawesome/free-solid-svg-icons';
import { GlobalConstants } from '@globalConstants';
import { IUser } from '@interfaces/IUser';
import { IAppState } from '@myStore/app.state';
import { Logout, ResetNotifications } from '@myStore/auth/auth.actions';
import { selectProfile } from '@myStore/profile-view/profile-view.selector';
import { SetTheme } from '@myStore/theme/theme.actions';
import { selectUsersUnconfirmedCount } from '@myStore/users/user.selector';
import { select, Store } from '@ngrx/store';
import { PermissionService } from '@services/permission/permission.service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  AccountantStudentControlItem,
  archiveMenuItem,
  CompaniesControlItem,
  CompaniesCoursesAndGroupsControlItem,
  coursesAndGroupsMenuItem,
  ESDPMenuItem,
  eventsMenuItem,
  materialsMenuItem,
  profileMenuItem,
  SalesApplicationsListItem,
  SalesContractCredentialControlItem,
  SalesCoursesGroupsControlItem,
  SalesDealsControlItem,
  SalesDiscountControlItem,
  SalesLeadsListItem,
  SalesManagersControlItem,
  SalesPresentationControlItem,
  studentWorksMenuItem,
  usersMenuItem,
} from '../menuItems';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.sass'],
  animations: [
    trigger('toggleHide', [
      state('in', style({ opacity: 1, transform: 'translateX(0)' })),

      transition(':enter', [
        style({ opacity: 0, width: 0, transform: 'translateX(-200px)' }),
        animate(300),
      ]),
      transition(
        ':leave',
        animate(
          300,
          style({ opacity: 0, width: 0, transform: 'translateX(-200px)' })
        )
      ),
    ]),
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  readonly faLeftArrow = faChevronLeft;
  readonly faRightArrow = faChevronRight;
  readonly faMenu = faBars;
  readonly sidebarSpawnWidth =
    GlobalConstants.SidebarSettings.sidebarSpawnWidth;
  readonly userRoles = GlobalConstants.UserRoles;
  public readonly isAlmaty =
    environment.baseUrl === GlobalConstants.ALMATY_BASE_URL;
  public collapse = true;
  public hide = false;
  public isMobile = false;
  public unconfirmedUsersCount = 0;
  public profile$: IUser | null = null;
  public commonMenu: {
    link: string;
    name: string;
    icon: IconDefinition;
    query?: Params | null;
  }[] = [profileMenuItem];
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<IAppState>,
    private _permissionsService: PermissionService
  ) {
    this.isMobile = document.body.clientWidth < this.sidebarSpawnWidth;
    this.hide = document.body.clientWidth < this.sidebarSpawnWidth;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = document.body.clientWidth < this.sidebarSpawnWidth;
    this.hide = document.body.clientWidth < this.sidebarSpawnWidth;
  }

  ngOnInit(): void {
    this._store
      .pipe(select(selectProfile), take(1), takeUntil(this.destroy$))
      .subscribe(profile => {
        if (profile) {
          this.profile$ = profile;

          this.createMenu();
        }
      });

    this._store
      .pipe(select(selectUsersUnconfirmedCount), takeUntil(this.destroy$))
      .subscribe(count => {
        this.unconfirmedUsersCount = count;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  createMenu() {
    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.admin,
      ])
    ) {
      this.commonMenu = [
        ...this.commonMenu,
        coursesAndGroupsMenuItem,
        usersMenuItem,
        CompaniesControlItem,
        eventsMenuItem,
        studentWorksMenuItem,
        materialsMenuItem,
        ESDPMenuItem,
        archiveMenuItem,
      ];
      return;
    }

    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.teacher,
      ])
    ) {
      this.commonMenu = [
        ...this.commonMenu,
        eventsMenuItem,
        studentWorksMenuItem,
        materialsMenuItem,
        usersMenuItem,
      ];

      return;
    }

    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.student,
      ])
    ) {
      this.commonMenu = [...this.commonMenu, eventsMenuItem];

      if (this.profile$.has_total_result) {
        this.commonMenu = [
          ...this.commonMenu,
          {
            link: `/users/${this.profile$.id}/final/results`,
            name: 'Приложение к сертификату',
            icon: faUserGraduate,
          },
        ];
      }
    }

    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.sales_admin,
        this.userRoles.sales_managers,
      ])
    ) {
      this.commonMenu = [
        ...this.commonMenu,
        SalesApplicationsListItem,
        SalesLeadsListItem,
        SalesDealsControlItem,
        SalesCoursesGroupsControlItem,
        SalesPresentationControlItem,
        SalesContractCredentialControlItem,
        SalesDiscountControlItem,
      ];

      if (
        this.profile$ &&
        this._permissionsService.checkPermission(this.profile$, [
          this.userRoles.sales_admin,
        ])
      ) {
        this.commonMenu = [...this.commonMenu, SalesManagersControlItem];
        return;
      }
      return;
    }

    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.accountant,
      ])
    ) {
      this.commonMenu = [
        ...this.commonMenu,
        AccountantStudentControlItem,
        SalesDealsControlItem,
        SalesCoursesGroupsControlItem,
        SalesDiscountControlItem,
      ];
      return;
    }

    if (
      this.profile$ &&
      this._permissionsService.checkPermission(this.profile$, [
        this.userRoles.companies,
      ])
    ) {
      this.commonMenu = [
        ...this.commonMenu,
        CompaniesCoursesAndGroupsControlItem,
      ];
      return;
    }
  }

  toggleHide(): void {
    this.hide = !this.hide;
    this.collapse = false;
  }

  toggleCollapse(): void {
    this.collapse = !this.collapse;
  }

  close(): void {
    if (this.isMobile) {
      this.toggleHide();
    } else {
      this.toggleCollapse();
    }
  }

  linkHandler(): void {
    if (this.isMobile) {
      this.toggleHide();
    }
  }

  setTheme(name: string): void {
    this._store.dispatch(new SetTheme(name));
  }

  logout(): void {
    this._store.dispatch(new ResetNotifications());
    this._store.dispatch(new Logout());
  }
}
